import React from 'react';
import { APP_STORE_URL, PLAY_STORE_URL } from '../lib/storeLinks';

/*
 * Premium custom store buttons that match the STON.FIT glass / purple aesthetic.
 * Self-contained SVG glyphs (Apple logo + colored Google Play triangle) so there
 * is no external image dependency. Sizing is driven off a single `height` prop.
 */

const AppleGlyph = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.05 12.54c-.02-2.05 1.68-3.03 1.75-3.08-.95-1.4-2.44-1.59-2.97-1.61-1.26-.13-2.47.74-3.11.74-.64 0-1.63-.72-2.68-.7-1.38.02-2.65.8-3.36 2.04-1.43 2.49-.37 6.17 1.03 8.19.68.99 1.5 2.1 2.57 2.06 1.03-.04 1.42-.66 2.67-.66 1.25 0 1.6.66 2.68.64 1.11-.02 1.81-1 2.49-2 .78-1.15 1.11-2.26 1.13-2.32-.02-.01-2.17-.83-2.19-3.29zM15.01 6.7c.57-.69.95-1.65.85-2.6-.82.03-1.81.55-2.4 1.23-.53.61-.99 1.58-.86 2.51.91.07 1.85-.46 2.41-1.14z" />
  </svg>
);

const PlayGlyph = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <defs>
      <linearGradient id="gpg-b" x1="3.9" y1="2.2" x2="12.6" y2="10.9" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#00A0FF" />
        <stop offset="1" stopColor="#00E3FF" />
      </linearGradient>
      <linearGradient id="gpg-y" x1="19.6" y1="12" x2="3.9" y2="12" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#FFE000" />
        <stop offset="1" stopColor="#FF9C00" />
      </linearGradient>
      <linearGradient id="gpg-r" x1="6.5" y1="13.7" x2="-.5" y2="21.2" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#FF3A44" />
        <stop offset="1" stopColor="#C31162" />
      </linearGradient>
      <linearGradient id="gpg-g" x1="-.5" y1="2.9" x2="6.5" y2="10.3" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#32A071" />
        <stop offset="1" stopColor="#00CF75" />
      </linearGradient>
    </defs>
    <path fill="url(#gpg-b)" d="M3.6 2.3c-.28.3-.44.76-.44 1.35v16.7c0 .59.16 1.05.44 1.35l.06.05L13 12.06v-.12L3.66 2.25z" />
    <path fill="url(#gpg-y)" d="M16.13 15.2 13 12.06v-.12l3.13-3.14.07.04 3.71 2.11c1.06.6 1.06 1.58 0 2.19l-3.71 2.11z" />
    <path fill="url(#gpg-r)" d="M16.2 15.16 13 11.94l-9.4 9.41c.35.37.93.42 1.58.05l11.02-6.24z" />
    <path fill="url(#gpg-g)" d="M16.2 8.84 5.18 2.6C4.53 2.23 3.95 2.28 3.6 2.65l9.4 9.29 3.2-3.1z" />
  </svg>
);

const StoreButton = ({ href, label, top, bottom, glyph, height, testid }) => {
  const iconSize = Math.round(height * 0.46);
  const topSize = Math.max(9, Math.round(height * 0.185));
  const bottomSize = Math.round(height * 0.3);
  const padX = Math.round(height * 0.44);
  const radius = Math.round(height * 0.32);

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      data-testid={testid}
      className="store-btn group relative inline-flex items-center overflow-hidden border border-white/10 text-white shadow-[0_1px_2px_rgba(0,0,0,0.3)] transition-all duration-300 ease-out hover:-translate-y-[3px] hover:border-white/25 hover:shadow-[0_12px_28px_-10px_rgba(0,0,0,0.55),0_6px_22px_-8px_rgba(147,51,234,0.22)]"
      style={{
        height,
        paddingLeft: padX,
        paddingRight: padX + 2,
        borderRadius: radius,
        gap: Math.round(height * 0.24),
        background:
          'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.025) 100%)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}
    >
      {/* Thin top sheen for a glassy edge */}
      <span
        className="pointer-events-none absolute inset-x-4 top-0 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.28), transparent)' }}
      />
      {/* Very soft purple aura behind the icon (brightens gently on hover) */}
      <span
        className="pointer-events-none absolute rounded-full opacity-40 transition-opacity duration-300 group-hover:opacity-70"
        style={{
          left: padX - 6,
          top: '50%',
          width: iconSize * 2,
          height: iconSize * 2,
          transform: 'translateY(-50%)',
          background: 'radial-gradient(circle, rgba(168,85,247,0.18) 0%, transparent 70%)',
          filter: 'blur(7px)',
        }}
      />
      <span
        className="relative flex shrink-0 items-center justify-center"
        style={{ color: '#fff', filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.35))' }}
      >
        {glyph(iconSize)}
      </span>
      <span className="relative flex flex-col justify-center leading-none">
        <span
          className="font-medium uppercase text-white/50"
          style={{ fontSize: topSize, letterSpacing: '0.09em' }}
        >
          {top}
        </span>
        <span
          className="font-semibold tracking-tight text-white/95"
          style={{ fontSize: bottomSize, marginTop: Math.round(height * 0.07) }}
        >
          {bottom}
        </span>
      </span>
    </a>
  );
};

const StoreBadges = ({ height = 52, className = '', align = 'center' }) => {
  const justify =
    align === 'start' ? 'justify-start' : align === 'end' ? 'justify-end' : 'justify-center';

  return (
    <div
      className={`flex flex-wrap items-center gap-4 sm:gap-5 ${justify} ${className}`}
      data-testid="store-badges"
    >
      <StoreButton
        href={APP_STORE_URL}
        label="Download STON.FIT on the App Store"
        top="Download on the"
        bottom="App Store"
        glyph={(s) => <AppleGlyph size={s} />}
        height={height}
        testid="app-store-badge"
      />
      <StoreButton
        href={PLAY_STORE_URL}
        label="Get STON.FIT on Google Play"
        top="Get it on"
        bottom="Google Play"
        glyph={(s) => <PlayGlyph size={s} />}
        height={height}
        testid="play-store-badge"
      />
    </div>
  );
};

export default StoreBadges;
